import Decimal from 'decimal.js';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { CargoContentsPerPortCall } from 'src/types/ViewModel/Stowage/Manifest/CargoContentsPerPortCall';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { isPositive } from '../decimalsUtils';
import { isBeforeOrEqual } from '../stowageRotationUtils';
import { isLoadingOrUnloadingCargoOperation } from '../cargoOperationUtils';
import { OperationType } from 'src/constants/stowage/operationTypes';

// Constants
const logCargoManifest: boolean = false;
export const DEFAULT_FIRST_VOYAGE_NUMBER: number = 202401;

// Types
interface UnloadingInfo {
    portCallGuid: string;
    portCallIndex: number;
    voyageNumber: string;
}

interface PortCallWithIndex {
    portCall: RotationPortCall;
    index: number;
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Gets the tank content key from berth call and tank
 */
const getTankContentByTankKey = (berthCall: RotationBerthCall, tank: Tank): string => `${berthCall.berthCallGuid}-${tank.tankId}`;

/**
 * Calculates the bill of lading weight for a cargo operation
 */
const getOperationBillOfLadingWeight = (op: RotationCargoOperation): Decimal => {
    const billOfLadingWeight = op.billOfLadingWeight ?? 0;
    const isPositiveOperation = op.operationType === OperationType.LOADING || op.operationType === OperationType.TRANSFER;
    return new Decimal(isPositiveOperation ? billOfLadingWeight : -billOfLadingWeight);
};

/**
 * Calculates the nominated weight for a cargo operation
 */
const getOperationNominatedWeight = (op: RotationCargoOperation): Decimal => {
    const nominatedWeight = op.nominatedWeight ?? 0;
    const isPositiveOperation = op.operationType === OperationType.LOADING || op.operationType === OperationType.TRANSFER;
    return new Decimal(isPositiveOperation ? nominatedWeight : -nominatedWeight);
};

/**
 * Creates a port call sequence with indices for ordering
 */
const createPortCallSequence = (stowagePlan: StowagePlan): PortCallWithIndex[] => stowagePlan.rotation.map((pc, index) => ({ portCall: pc, index }));

/**
 * Maps unloading operations to determine the last unloading port for each cargo
 */
const mapUnloadingOperations = (
    portCallSequence: PortCallWithIndex[]
): {
    unloadedCargoes: Set<string>;
    unloadingPortCalls: Map<string, UnloadingInfo>;
} => {
    const unloadedCargoes = new Set<string>();
    const unloadingPortCalls = new Map<string, UnloadingInfo>();
    const cargoUnloadingOps = new Map<string, { portCall: RotationPortCall; portCallIndex: number; berthCall: RotationBerthCall }[]>(); // Collect all unloading operations for each cargo
    portCallSequence.forEach(({ portCall, index }) => {
        for (const berthCall of portCall.berthCalls) {
            for (const op of berthCall.cargoOperations) {
                if (op.operationType !== 'Unloading') continue;

                const cargoUnloadings = cargoUnloadingOps.get(op.cargoGuid) || [];
                cargoUnloadings.push({ portCall, portCallIndex: index, berthCall });
                cargoUnloadingOps.set(op.cargoGuid, cargoUnloadings);
            }
        }
    });

    // Find the last unloading port for each cargo
    cargoUnloadingOps.forEach((unloadings, cargoGuid) => {
        if (!unloadings.length) return;

        // Sort by port call index to find the last unloading
        unloadings.sort((a, b) => b.portCallIndex - a.portCallIndex);
        const lastUnloading = unloadings[0];

        // Mark this cargo as unloaded
        unloadedCargoes.add(cargoGuid); // Store information about the last unloading port call
        unloadingPortCalls.set(cargoGuid, {
            portCallGuid: lastUnloading.portCall.portCallGuid,
            portCallIndex: lastUnloading.portCallIndex,
            voyageNumber: lastUnloading.portCall.voyageNumber,
        });
    });

    return { unloadedCargoes, unloadingPortCalls };
};

/**
 * Checks if current port call is after the cargo's unloading port
 */
const isAfterCargoUnloading = (cargoGuid: string, currentPortCallIndex: number, unloadingPortCalls: Map<string, UnloadingInfo>): boolean => {
    const lastUnloading = unloadingPortCalls.get(cargoGuid);
    return Boolean(lastUnloading && currentPortCallIndex > lastUnloading.portCallIndex);
};

/**
 * Checks if a cargo should be carried forward based on its unloading status
 */
const shouldCarryForwardCargo = (
    cargoGuid: string,
    currentPortCallIndex: number,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    isBeforeCutoff: boolean,
    cutoffVoyage: number,
    portCallVoyageNumber: string,
    cargo?: { voyageNumber: string }
): boolean => {
    const lastUnloading = unloadingPortCalls.get(cargoGuid);

    // If no unloading found, check cut-off logic based on cargo's voyage number
    if (!lastUnloading) {
        if (cargo) {
            const cargoVoyageNumber = Number(cargo.voyageNumber);
            const currentVoyageNumber = Number(portCallVoyageNumber);

            // If cargo is from before cut-off and we're at/after cut-off, don't carry forward
            if (cargoVoyageNumber < cutoffVoyage && currentVoyageNumber >= cutoffVoyage) {
                return false;
            }
        }
        return true;
    }

    // If current port call is at or before the unloading port, cargo should be carried forward
    const isAtOrBeforeUnloadingPort = currentPortCallIndex <= lastUnloading.portCallIndex;
    if (isAtOrBeforeUnloadingPort) {
        return true;
    }

    // After unloading port: only carry forward if cargo was unloaded at/after cut-off
    // If cargo was unloaded before cut-off and we're at/after cut-off, don't carry forward
    const currentVoyageNumber = Number(portCallVoyageNumber);
    const wasCargoUnloadedBeforeCutoff = lastUnloading.voyageNumber && Number(lastUnloading.voyageNumber) < cutoffVoyage;
    const isCurrentPortAtOrAfterCutoff = currentVoyageNumber >= cutoffVoyage;

    if (wasCargoUnloadedBeforeCutoff && isCurrentPortAtOrAfterCutoff) {
        return false;
    }

    return true;
};

/**
 * Gets the adjusted weight for a cargo based on unloading status
 */
const getAdjustedCargoWeight = (
    cargoGuid: string,
    currentWeight: Decimal,
    currentPortCallIndex: number,
    unloadingPortCalls: Map<string, UnloadingInfo>
): Decimal => {
    const isAfterUnloading = isAfterCargoUnloading(cargoGuid, currentPortCallIndex, unloadingPortCalls);
    const adjustedWeight = isAfterUnloading ? new Decimal(0) : currentWeight;
    return adjustedWeight;
};

/**
 * Creates a transit cargo operation from content
 */
const createTransitOperation = (content: CargoContent, transitBerthCall: RotationBerthCall): RotationCargoOperation => {
    // Use transit-specific BoL (excludes current port's loading BoL) if available, otherwise fall back to total BoL
    const transitBoL = content.billOfLadingWeightInTransit ?? content.billOfLadingWeight;
    return {
        berthCall: transitBerthCall,
        berthCallGuid: transitBerthCall.berthCallGuid,
        portCallGuid: transitBerthCall.portCall.portCallGuid,
        cargoOperationGuid: content.cargoOperation?.cargoOperationGuid ?? crypto.randomUUID(),
        cargoGuid: content.cargo.cargoGuid,
        cargo: content.cargo,
        cargoOperationSequenceNumber: null,
        billOfLadingWeight: transitBoL?.toNumber() ?? null,
        operationType: 'Transit',
        contractWeight: null,
        nominatedWeight: content.weightInTransit.toNumber(),
        dataSource: 'OrcaWeb',
    };
};

// ============================================================
// Public API Functions
// ============================================================

export const getCutOffVoyageNumber = (vessel: Vessel): number => {
    return vessel?.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER;
};

/**
 * Updates the manifest for a stowage plan
 */
export const updateManifest = (stowagePlan: StowagePlan, vessel: Vessel): void => {
    // Initialize manifest
    stowagePlan.manifest = {
        cargoContentsPerPortCall: [],
        cargoContentsPerBerthCall: [],
        tankContentsByTankPerBerthCall: new Map<string, TankContent>(),
    };

    // Initialize necessary fields
    stowagePlan.transitOperations = [];
    stowagePlan.transitStowageItems = [];

    // Process in a single pipeline with shared unloading information
    const cutoffVoyage = getCutOffVoyageNumber(vessel);
    const portCallSequence = createPortCallSequence(stowagePlan);
    const { unloadedCargoes, unloadingPortCalls } = mapUnloadingOperations(portCallSequence); // Build manifest in steps
    updateCargoContentsByPortCall(stowagePlan, cutoffVoyage);
    updateTankContentsByTank(stowagePlan, vessel, unloadingPortCalls, cutoffVoyage, portCallSequence);
    updateTransitOperations(stowagePlan, unloadedCargoes, unloadingPortCalls, cutoffVoyage, portCallSequence);
    updateTankContentsWithTransit(stowagePlan);
    addTransitOperationsStowageItems(stowagePlan);
};

// Other public functions remain unchanged
export const getFilteredTankContent = (tankContent: TankContent, operationTypes: string[]): TankContent => {
    if (!tankContent?.cargoContents) return null;

    const cargoContents = tankContent.cargoContents.filter((o) => operationTypes.includes(o.cargoOperation?.operationType));
    if (!cargoContents?.length) return null;

    return {
        portCall: tankContent.portCall,
        berthCall: tankContent.berthCall,
        tank: tankContent.tank,
        cargoContents: cargoContents,
    };
};

export const getTankContentsInPortCall = (stowagePlan: StowagePlan, portCall: RotationPortCall, tank: Tank): TankContent[] => {
    const result: TankContent[] = [];
    if (!stowagePlan?.manifest?.tankContentsByTankPerBerthCall) return result;

    stowagePlan.manifest.tankContentsByTankPerBerthCall.forEach((tankContent) => {
        if (tankContent.portCall.portCallGuid === portCall.portCallGuid && tankContent.tank?.tankId === tank.tankId) {
            result.push(tankContent);
        }
    });

    return result;
};

export const getTankContentInBerthCall = (stowagePlan: StowagePlan, berthCall: RotationBerthCall | null, tank: Tank): TankContent => {
    if (!stowagePlan?.manifest?.tankContentsByTankPerBerthCall || !berthCall || !tank) return null;
    const key = `${berthCall.berthCallGuid}-${tank.tankId}`;
    const tankContent = stowagePlan.manifest.tankContentsByTankPerBerthCall.get(key) || null;

    return tankContent;
};

export const getFilteredCargoContents = (cargoContents: CargoContent[], operationTypes: string[]): CargoContent[] => {
    if (!cargoContents?.length) return null;

    const _cargoContents = [...cargoContents].filter((o) => operationTypes.includes(o.cargoOperation?.operationType));
    if (!_cargoContents?.length) return null;

    return _cargoContents;
};

export const getFilteredTankContents = (tankContents: TankContent[], operationTypes: string[]): TankContent => {
    if (!tankContents?.length) return null;

    const cargoContents = tankContents.flatMap(
        (o) => o?.cargoContents?.filter((c) => operationTypes.includes(c.cargoOperation?.operationType)) || []
    );

    if (!cargoContents?.length) return null;

    // Deduplicate by cargoGuid — the same cargo carried through multiple berth calls
    // should only appear once (e.g., transit cargo exists in every berth call of a port)
    const seen = new Set<string>();
    const deduplicatedContents = cargoContents.filter((c) => {
        const key = c.cargo?.cargoGuid;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    if (!deduplicatedContents.length) return null;

    return {
        portCall: tankContents[0].portCall,
        berthCall: tankContents[0].berthCall,
        tank: tankContents[0].tank,
        cargoContents: deduplicatedContents,
    };
};
export const getCargoContentsInPortCall = (stowagePlan: StowagePlan, portCall: RotationPortCall): CargoContent[] => {
    const result: CargoContent[] = [];
    if (!stowagePlan?.manifest?.cargoContentsPerPortCall) return result;

    stowagePlan.manifest.cargoContentsPerPortCall.forEach((cargoContent) => {
        if (cargoContent.portCallGuid === portCall.portCallGuid) {
            result.push(...cargoContent.cargoContents);
        }
    });

    return result;
};
export const getCargoContentsInBerthCall = (stowagePlan: StowagePlan, berthCall: RotationBerthCall): CargoContent[] => {
    const result: CargoContent[] = [];
    const cargoContentsPerPortCall = getCargoContentsInPortCall(stowagePlan, berthCall.portCall);
    if (!cargoContentsPerPortCall?.length) return result;

    const cargoContentsPerBerthCall = cargoContentsPerPortCall.filter((c) => c.cargoOperation?.berthCallGuid === berthCall.berthCallGuid);

    result.push(...cargoContentsPerBerthCall);

    return result;
};

export const getPreviousDistinctCargoContentsInTank = (
    stowagePlan: StowagePlan,
    tank: Tank,
    berthCall: RotationBerthCall,
    maxNumberOfCargoes: number,
    includeCurrentCargo: boolean = false
): CargoContent[] => {
    const previousTankContents = getPreviousTankContentsInTank(stowagePlan, tank, berthCall);

    const allCargoContents: CargoContent[] = [];

    // If includeCurrentCargo is true, add current cargo contents first (only Transit operations)
    if (includeCurrentCargo) {
        const currentTankContent = getTankContentInBerthCall(stowagePlan, berthCall, tank);
        if (currentTankContent?.cargoContents?.length) {
            // Only include Transit operations as previous cargo, not Loaded cargo
            const transitCargoes = currentTankContent.cargoContents.filter((content) => content.cargoOperation?.operationType === 'Transit');
            allCargoContents.push(...transitCargoes);
        }
    }

    // Add previous cargo contents
    if (previousTankContents?.length) {
        const previousCargoContents = previousTankContents.flatMap((tankContent) => tankContent.cargoContents);
        allCargoContents.push(...previousCargoContents);
    }

    if (!allCargoContents?.length) return [];

    // Create a map to deduplicate by cargo GUID and convert back to array
    const distinctCargoContents = Array.from(new Map(allCargoContents.map((content) => [content.cargo.cargoGuid, content])).values());

    // Sort primarily by voyage number (highest first), with berth call order as fallback for same voyage
    const sortedCargoContents = distinctCargoContents.sort((a, b) => {
        // Primary sort: voyage number (highest first)
        const voyageComparison = +b.cargo.voyageNumber - +a.cargo.voyageNumber;
        if (voyageComparison !== 0) {
            return voyageComparison;
        }

        // Secondary sort: berth call chronological order (reverse order for same voyage)
        const loadingBerthA =
            stowagePlan.cargoOperations?.find((op) => op.cargoGuid === a.cargo.cargoGuid && op.operationType === 'Loading')?.berthCall ??
            a.cargoOperation?.berthCall;
        const loadingBerthB =
            stowagePlan.cargoOperations?.find((op) => op.cargoGuid === b.cargo.cargoGuid && op.operationType === 'Loading')?.berthCall ??
            b.cargoOperation?.berthCall;

        if (loadingBerthA && loadingBerthB && stowagePlan.berthCalls) {
            const aBeforeB = isBeforeOrEqual(stowagePlan.berthCalls, loadingBerthA, loadingBerthB);
            const bBeforeA = isBeforeOrEqual(stowagePlan.berthCalls, loadingBerthB, loadingBerthA);

            if (aBeforeB && !bBeforeA) return 1; // A is before B, so B should come first (reverse order)
            if (bBeforeA && !aBeforeB) return -1; // B is before A, so A should come first (reverse order)
        }

        return 0; // If berth calls are equal or missing, maintain existing order
    });

    return sortedCargoContents.slice(0, maxNumberOfCargoes);
};

export const getPreviousDistinctCargoContentsInMultipleTanks = (
    stowagePlan: StowagePlan,
    tanks: Tank[],
    berthCall: RotationBerthCall,
    maxNumberOfCargoes: number,
    includeCurrentCargo: boolean = false
): Map<number, CargoContent[]> => {
    const tankContents = new Map<number, CargoContent[]>();
    if (!tanks?.length || !stowagePlan || !berthCall) return tankContents;

    for (const tank of tanks) {
        const cargoContents = getPreviousDistinctCargoContentsInTank(stowagePlan, tank, berthCall, maxNumberOfCargoes, includeCurrentCargo);
        tankContents.set(tank.tankId, cargoContents);
    }

    return tankContents;
};

// ============================================================
// Core Processing Functions
// ============================================================

/**
 * Updates cargo contents by port call
 */
const updateCargoContentsByPortCall = (stowagePlan: StowagePlan, cutoffVoyage: number): void => {
    if (logCargoManifest) {
        console.log('stowagePlan:', stowagePlan);
        console.log('stowagePlan.rotation:', stowagePlan.rotation);
    }

    // Single-pass approach combining initialization and processing
    const manifestItemsMap = new Map<string, CargoContentsPerPortCall>();
    let previousPortCallGuid: string | null = null;
    const previousWeights = new Map<string, { weight: Decimal; blWeight: Decimal }>();

    stowagePlan.rotation.forEach((portCall: RotationPortCall) => {
        // Initialize manifest item
        const manifestItem: CargoContentsPerPortCall = {
            portCallGuid: portCall.portCallGuid,
            cargoContents: [],
        };
        manifestItemsMap.set(portCall.portCallGuid, manifestItem);
        stowagePlan.manifest.cargoContentsPerPortCall.push(manifestItem);

        // Aggregate all operations per cargo in current port call
        const currentCargoWeights = new Map<
            string,
            {
                ops: RotationCargoOperation[];
                weight: Decimal;
                blWeight: Decimal;
            }
        >();

        // Process all cargo operations in all berth calls at once
        for (const berthCall of portCall.berthCalls) {
            for (const op of berthCall.cargoOperations) {
                if (!isLoadingOrUnloadingCargoOperation(op)) continue;

                let cargoData = currentCargoWeights.get(op.cargo.cargoGuid);
                if (!cargoData) {
                    cargoData = {
                        ops: [op],
                        weight: getOperationNominatedWeight(op),
                        blWeight: getOperationBillOfLadingWeight(op),
                    };
                    currentCargoWeights.set(op.cargo.cargoGuid, cargoData);
                } else {
                    cargoData.ops.push(op);
                    cargoData.weight = cargoData.weight.plus(getOperationNominatedWeight(op));
                    cargoData.blWeight = cargoData.blWeight.plus(getOperationBillOfLadingWeight(op));
                }
            }
        }

        // Create cargo contents and update with previous weights
        const newWeights = new Map<string, { weight: Decimal; blWeight: Decimal }>();

        currentCargoWeights.forEach((data, cargoGuid) => {
            const prevWeight = previousWeights.get(cargoGuid);
            const totalWeight = prevWeight ? data.weight.plus(prevWeight.weight) : data.weight;
            const totalBLWeight = prevWeight ? data.blWeight.plus(prevWeight.blWeight) : data.blWeight;

            const weightInTransit = prevWeight
                ? data.weight.lt(new Decimal(0))
                    ? data.weight.plus(prevWeight.weight)
                    : prevWeight.weight
                : new Decimal(0);

            // Compute transit BoL using the same logic as weightInTransit but for BoL values
            const billOfLadingWeightInTransit = prevWeight
                ? data.blWeight.lt(new Decimal(0))
                    ? data.blWeight.plus(prevWeight.blWeight)
                    : prevWeight.blWeight
                : new Decimal(0);

            if (isPositive(totalWeight)) {
                manifestItem.cargoContents.push({
                    cargoOperation: data.ops[0],
                    cargo: data.ops[0].cargo,
                    weight: totalWeight,
                    weightInTransit,
                    billOfLadingWeight: totalBLWeight,
                    billOfLadingWeightInTransit,
                    stowage: null,
                });
                newWeights.set(cargoGuid, { weight: totalWeight, blWeight: totalBLWeight });
            }
        }); // Add missing previous cargoes (applying cut-off voyage filtering)
        if (previousWeights.size > 0) {
            const currentVoyageNumber = Number(portCall.voyageNumber);
            const isAtOrAfterCutoff = currentVoyageNumber >= cutoffVoyage;

            // Build Map for O(1) lookup of previous manifest cargoes
            const previousManifestItem = manifestItemsMap.get(previousPortCallGuid);
            const previousCargoByGuid = new Map<string, CargoContent>();
            if (previousManifestItem) {
                for (const c of previousManifestItem.cargoContents) {
                    if (!previousCargoByGuid.has(c.cargo.cargoGuid)) {
                        previousCargoByGuid.set(c.cargo.cargoGuid, c);
                    }
                }
            }

            previousWeights.forEach((prevData, cargoGuid) => {
                if (!currentCargoWeights.has(cargoGuid) && isPositive(prevData.weight)) {
                    const previousCargo = previousCargoByGuid.get(cargoGuid);

                    // Apply cut-off voyage filtering for legacy cargoes
                    if (isAtOrAfterCutoff && previousCargo?.cargo?.voyageNumber) {
                        const cargoVoyageNumber = Number(previousCargo.cargo.voyageNumber);
                        // Skip legacy cargoes (from before cut-off) when we're at/after cut-off voyage
                        if (cargoVoyageNumber < cutoffVoyage) {
                            return; // Don't add this legacy cargo to manifest
                        }
                    }

                    manifestItem.cargoContents.push({
                        cargoOperation: null,
                        cargo: previousCargo.cargo,
                        weight: prevData.weight,
                        weightInTransit: prevData.weight,
                        billOfLadingWeight: prevData.blWeight,
                        billOfLadingWeightInTransit: prevData.blWeight,
                        stowage: null,
                    });
                    newWeights.set(cargoGuid, prevData);
                }
            });
        }

        previousPortCallGuid = portCall.portCallGuid;
        previousWeights.clear();
        newWeights.forEach((data, cargoGuid) => {
            previousWeights.set(cargoGuid, data);
        });
    });
};

/**
 * Updates tank contents by tank with unloading information
 */
const updateTankContentsByTank = (
    stowagePlan: StowagePlan,
    vessel: Vessel,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    cutoffVoyage: number,
    portCallSequence: PortCallWithIndex[]
): void => {
    // Add stowage items to tanks
    populateTanksWithStowageItems(stowagePlan);

    // Process tanks with previous content considerations
    processTankContents(stowagePlan, vessel, unloadingPortCalls, cutoffVoyage, portCallSequence);
};

/**
 * Populates tank contents with stowage items
 */
const populateTanksWithStowageItems = (stowagePlan: StowagePlan): void => {
    for (const stowage of stowagePlan.stowageItems) {
        const berthCall = stowage?.cargoOperation?.berthCall;
        const portCall = berthCall?.portCall;
        if (!portCall || !berthCall) continue;

        let tankContent = getTankContentInBerthCall(stowagePlan, berthCall, stowage.tank);
        if (!tankContent) {
            tankContent = {
                portCall,
                berthCall,
                tank: stowage.tank,
                cargoContents: [],
            };
            stowagePlan.manifest.tankContentsByTankPerBerthCall.set(`${berthCall.berthCallGuid}-${tankContent.tank.tankId}`, tankContent);
        }

        const existingContent = tankContent.cargoContents.find((c) => c.cargo.cargoGuid === stowage.cargoOperation.cargoGuid);
        if (existingContent) {
            existingContent.weight = existingContent.weight.add(stowage.weight);
            existingContent.stowage = stowage;
        } else {
            tankContent.cargoContents.push({
                cargoOperation: stowage.cargoOperation,
                cargo: stowage.cargoOperation.cargo,
                weight: stowage.weight,
                weightInTransit: new Decimal(0),
                billOfLadingWeight: null,
                shipFigure: stowage.shipFigure,
                isShipFigureAdjusted: stowage.isShipFigureAdjusted,
                stowage: stowage,
            });
        }
    }
};

/**
 * Processes tank contents considering previous contents and unloading
 */
const processTankContents = (
    stowagePlan: StowagePlan,
    vessel: Vessel,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    cutoffVoyage: number,
    portCallSequence: PortCallWithIndex[]
): void => {
    let previousBerthCall: RotationBerthCall = null;

    // Pre-build index: berthCallGuid -> tankId -> Set<cargoGuid> for O(1) lookups
    const stowageItemIndex = new Map<string, Map<number, Set<string>>>();
    for (const si of stowagePlan.stowageItems) {
        const bcGuid = si.cargoOperation?.berthCallGuid;
        if (!bcGuid) continue;
        let tankMap = stowageItemIndex.get(bcGuid);
        if (!tankMap) {
            tankMap = new Map<number, Set<string>>();
            stowageItemIndex.set(bcGuid, tankMap);
        }
        let cargoSet = tankMap.get(si.tank.tankId);
        if (!cargoSet) {
            cargoSet = new Set<string>();
            tankMap.set(si.tank.tankId, cargoSet);
        }
        if (si.cargoOperation?.cargoGuid) {
            cargoSet.add(si.cargoOperation.cargoGuid);
        }
    }

    for (const { portCall, index: currentPortCallIndex } of portCallSequence) {
        const isBeforeCutoff = cutoffVoyage && Number(portCall.voyageNumber) < cutoffVoyage;

        for (const berthCall of portCall.berthCalls) {
            for (const tank of vessel.cargoTanks) {
                const currentKey = getTankContentByTankKey(berthCall, tank);
                const tankContent = stowagePlan.manifest.tankContentsByTankPerBerthCall.get(currentKey);

                const previousKey = previousBerthCall ? getTankContentByTankKey(previousBerthCall, tank) : null;
                const previousTankContent = previousKey ? stowagePlan.manifest.tankContentsByTankPerBerthCall.get(previousKey) : null;

                if (tankContent && previousTankContent) {
                    updateExistingTankContent(
                        tankContent,
                        previousTankContent,
                        unloadingPortCalls,
                        currentPortCallIndex,
                        isBeforeCutoff,
                        cutoffVoyage,
                        portCall.voyageNumber
                    );
                } else if (previousTankContent) {
                    createNewTankContentFromPrevious(
                        stowagePlan,
                        previousTankContent,
                        portCall,
                        berthCall,
                        tank,
                        unloadingPortCalls,
                        currentPortCallIndex,
                        isBeforeCutoff,
                        cutoffVoyage,
                        stowageItemIndex
                    );
                }
            }
            previousBerthCall = berthCall;
        }
    }
};

/**
 * Updates existing tank content with previous content and handles unloading
 */
const updateExistingTankContent = (
    tankContent: TankContent,
    previousTankContent: TankContent,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    currentPortCallIndex: number,
    isBeforeCutoff: boolean,
    cutoffVoyage: number,
    portCallVoyageNumber: string
): void => {
    const cargoGuidsInTank = new Set(tankContent.cargoContents.map((c) => c.cargo.cargoGuid));

    // Build Map for O(1) lookup of previous cargo contents
    const previousCargoByGuid = new Map<string, CargoContent>();
    for (const c of previousTankContent.cargoContents) {
        if (!previousCargoByGuid.has(c.cargo.cargoGuid)) {
            previousCargoByGuid.set(c.cargo.cargoGuid, c);
        }
    }

    // Process existing content
    for (const content of tankContent.cargoContents) {
        const cargoGuid = content.cargo.cargoGuid;
        const isAfterUnloading = isAfterCargoUnloading(cargoGuid, currentPortCallIndex, unloadingPortCalls);

        // Zero out weight if after unloading (except for unloading operations)
        if (isAfterUnloading && content.cargoOperation?.operationType !== 'Unloading') {
            content.weight = new Decimal(0);
            content.weightInTransit = new Decimal(0);
            continue;
        }

        // Update weights from previous content
        const previousCargoContent = previousCargoByGuid.get(cargoGuid);
        if (!previousCargoContent) continue;

        const prevWeight = previousCargoContent.weight || new Decimal(0);
        content.weightInTransit = content.weightInTransit.plus(content.weight.lt(new Decimal(0)) ? content.weight.plus(prevWeight) : prevWeight);
        content.weight = content.weight.plus(prevWeight); // Update bill of lading weight if available
        if (content.billOfLadingWeight && previousCargoContent.billOfLadingWeight) {
            content.billOfLadingWeight = content.billOfLadingWeight.plus(previousCargoContent.billOfLadingWeight);
        }

        // Update ship figure if available
        if (
            content.shipFigure !== null &&
            content.shipFigure !== undefined &&
            previousCargoContent.shipFigure !== null &&
            previousCargoContent.shipFigure !== undefined
        ) {
            content.shipFigure = content.shipFigure + previousCargoContent.shipFigure;
            content.isShipFigureAdjusted = content.isShipFigureAdjusted || previousCargoContent.isShipFigureAdjusted;
        }
    } // Add missing previous cargoes if appropriate
    addMissingPreviousCargoes(
        tankContent,
        previousTankContent,
        cargoGuidsInTank,
        unloadingPortCalls,
        currentPortCallIndex,
        isBeforeCutoff,
        cutoffVoyage,
        portCallVoyageNumber
    );
};

/**
 * Adds missing cargoes from previous tank content if they should be carried forward
 */
const addMissingPreviousCargoes = (
    tankContent: TankContent,
    previousTankContent: TankContent,
    existingCargoGuids: Set<string>,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    currentPortCallIndex: number,
    isBeforeCutoff: boolean,
    cutoffVoyage: number,
    portCallVoyageNumber: string
): void => {
    for (const prevContent of previousTankContent.cargoContents) {
        const cargoGuid = prevContent.cargo.cargoGuid;
        if (existingCargoGuids.has(cargoGuid)) continue;
        if (
            shouldCarryForwardCargo(
                cargoGuid,
                currentPortCallIndex,
                unloadingPortCalls,
                isBeforeCutoff,
                cutoffVoyage,
                portCallVoyageNumber,
                prevContent.cargo
            )
        ) {
            const weightToUse = getAdjustedCargoWeight(cargoGuid, prevContent.weight, currentPortCallIndex, unloadingPortCalls);

            tankContent.cargoContents.push({
                ...prevContent,
                weight: weightToUse,
                weightInTransit: weightToUse,
                cargoOperation: null, // Will be updated later
            });
        }
    }
};

/**
 * Creates new tank content from previous tank content
 */
const createNewTankContentFromPrevious = (
    stowagePlan: StowagePlan,
    previousTankContent: TankContent,
    portCall: RotationPortCall,
    berthCall: RotationBerthCall,
    tank: Tank,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    currentPortCallIndex: number,
    isBeforeCutoff: boolean,
    cutoffVoyage: number,
    stowageItemIndex: Map<string, Map<number, Set<string>>>
): void => {
    // Filter out cargoes that shouldn't be carried forward
    const filteredCargoContents = previousTankContent.cargoContents.filter((prevContent) => {
        return shouldCarryForwardCargo(
            prevContent.cargo.cargoGuid,
            currentPortCallIndex,
            unloadingPortCalls,
            isBeforeCutoff,
            cutoffVoyage,
            portCall.voyageNumber,
            prevContent.cargo
        );
    });

    if (filteredCargoContents.length === 0) {
        return;
    }

    // Check if this tank has any operations at the current berth using pre-built index
    const cargoGuidsInTankAtBerth = stowageItemIndex.get(berthCall.berthCallGuid)?.get(tank.tankId);
    const tankHasOperationAtBerth =
        berthCall.cargoOperations.length > 0 &&
        cargoGuidsInTankAtBerth !== undefined &&
        filteredCargoContents.some((fc) => cargoGuidsInTankAtBerth.has(fc.cargo.cargoGuid));

    // Adjust weights for cargoes after their unloading port
    const adjustedCargoContents = filteredCargoContents.map((prevContent) => {
        // If this tank has no operation at this berth, carry forward the weight as-is
        // This handles partial unloading scenarios where some tanks are emptied but others still have cargo
        const weightToUse = tankHasOperationAtBerth
            ? getAdjustedCargoWeight(prevContent.cargo.cargoGuid, prevContent.weight, currentPortCallIndex, unloadingPortCalls)
            : prevContent.weight;

        return weightToUse.equals(prevContent.weight)
            ? prevContent
            : {
                  ...prevContent,
                  weight: weightToUse,
                  weightInTransit: weightToUse,
              };
    });

    // Create new tank content
    const newTankContent: TankContent = {
        portCall,
        berthCall,
        tank,
        cargoContents: adjustedCargoContents.map((prev) => ({
            cargoOperation: null, // Will be updated later
            cargo: prev.cargo,
            weight: prev.weight,
            weightInTransit: prev.weight,
            billOfLadingWeight: null,
            shipFigure: prev.shipFigure,
            isShipFigureAdjusted: prev.isShipFigureAdjusted,
            stowage: prev.stowage,
        })),
    };

    if (newTankContent.cargoContents.length > 0) {
        const key = getTankContentByTankKey(berthCall, tank);
        stowagePlan.manifest.tankContentsByTankPerBerthCall.set(key, newTankContent);
    }
};

/**
 * Updates transit operations for a stowage plan
 */
const updateTransitOperations = (
    stowagePlan: StowagePlan,
    unloadedCargoes: Set<string>,
    unloadingPortCalls: Map<string, UnloadingInfo>,
    cutoffVoyage: number,
    portCallSequence: PortCallWithIndex[]
): void => {
    // Pre-compute manifest items and port call indices for performance
    const manifestItemsMap = new Map<string, CargoContentsPerPortCall>();
    stowagePlan.manifest.cargoContentsPerPortCall.forEach((item) => {
        manifestItemsMap.set(item.portCallGuid, item);
    });
    const portCallIndexMap = new Map<string, number>();
    portCallSequence.forEach((pc, idx) => {
        portCallIndexMap.set(pc.portCall.portCallGuid, idx);
    });

    // Generate transit operations for each port call
    stowagePlan.transitOperations = stowagePlan.rotation.map((portCall: RotationPortCall) => {
        const manifestItem = manifestItemsMap.get(portCall.portCallGuid);
        if (!manifestItem?.cargoContents?.length) return createEmptyTransitPortCall(portCall);

        const isBeforeCutoff = Number(portCall.voyageNumber) < cutoffVoyage;
        const isAtOrAfterCutoff = Number(portCall.voyageNumber) >= cutoffVoyage;
        const transitBerthCall = portCall.berthCalls[portCall.berthCalls.length - 1];
        if (transitBerthCall && !transitBerthCall.portCall) {
            transitBerthCall.portCall = portCall;
        }
        const currentPortCallIndex = portCallIndexMap.get(portCall.portCallGuid) ?? -1;

        // Filter cargo contents that should have transit operations
        const validTransitContents = manifestItem.cargoContents.filter((content) => {
            if (!isPositive(content?.weightInTransit)) return false;

            const cargoGuid = content.cargo.cargoGuid;
            const cargoVoyageNumber = Number(content.cargo.voyageNumber);

            // Skip transit operations for cargoes with voyage numbers prior to cut-off
            // if the port call is at or after the cut-off voyage
            if (isAtOrAfterCutoff && cargoVoyageNumber < cutoffVoyage) {
                return false;
            }
            return (
                shouldCarryForwardCargo(
                    cargoGuid,
                    currentPortCallIndex,
                    unloadingPortCalls,
                    isBeforeCutoff,
                    cutoffVoyage,
                    portCall.voyageNumber,
                    content.cargo
                ) || !unloadedCargoes.has(cargoGuid)
            );
        });

        // Create transit operations
        transitBerthCall.cargoOperations = validTransitContents.map((content) => createTransitOperation(content, transitBerthCall));

        // Return transit port call
        return {
            portDisplayName: portCall.portDisplayName,
            portCallGuid: portCall.portCallGuid,
            voyageNumber: portCall.voyageNumber,
            berthCalls: [transitBerthCall],
        } as RotationPortCall;
    });

    if (logCargoManifest) {
        logTransitOperations(stowagePlan);
    }
};

/**
 * Creates an empty transit port call
 */
const createEmptyTransitPortCall = (portCall: RotationPortCall): RotationPortCall => {
    const transitBerthCall = portCall.berthCalls[portCall.berthCalls.length - 1];
    transitBerthCall.cargoOperations = [];

    return {
        portDisplayName: portCall.portDisplayName,
        portCallGuid: portCall.portCallGuid,
        voyageNumber: portCall.voyageNumber,
        berthCalls: [transitBerthCall],
    } as RotationPortCall;
};

/**
 * Logs transit operations summary
 */
const logTransitOperations = (stowagePlan: StowagePlan): void => {
    console.log('===== TRANSIT OPERATIONS SUMMARY =====');
    stowagePlan.transitOperations.forEach((portCall) => {
        console.log(
            `Port: ${portCall.portDisplayName}, Voyage: ${portCall.voyageNumber}, Operations: ${portCall.berthCalls[0].cargoOperations.length}`
        );
        portCall.berthCalls.forEach((berthCall) => {
            berthCall.cargoOperations.forEach((op) => {
                console.log(`  - Cargo: ${op.cargo.cargoNumber}, Voyage: ${op.cargo.voyageNumber}, Weight: ${op.nominatedWeight}`);
            });
        });
    });
};

/**
 * Updates tank contents with transit operations
 */
const updateTankContentsWithTransit = (stowagePlan: StowagePlan): void => {
    // Build transit operations map
    const transitOperationsMap = buildTransitOperationsMap(stowagePlan);

    // Update tank contents with transit operations
    updateTankContentsWithTransitOps(stowagePlan, transitOperationsMap);

    // Filter out zero weight items and empty tank contents
    filterEmptyTankContents(stowagePlan);
};

/**
 * Builds a map of transit operations by port call
 */
const buildTransitOperationsMap = (stowagePlan: StowagePlan): Map<string, RotationCargoOperation[]> => {
    const transitOperationsMap = new Map<string, RotationCargoOperation[]>();

    if (!stowagePlan.transitOperations?.length) return transitOperationsMap;

    for (const portCall of stowagePlan.transitOperations) {
        if (portCall.berthCalls?.length > 0) {
            transitOperationsMap.set(portCall.portCallGuid, portCall.berthCalls[0].cargoOperations || []);
        }
    }

    return transitOperationsMap;
};

/**
 * Updates tank contents with transit operations from map
 */
const updateTankContentsWithTransitOps = (stowagePlan: StowagePlan, transitOperationsMap: Map<string, RotationCargoOperation[]>): void => {
    // Pre-build Map<portCallGuid, Map<cargoGuid, transitOp>> for O(1) lookups
    const transitOpsByPortAndCargo = new Map<string, Map<string, RotationCargoOperation>>();
    transitOperationsMap.forEach((ops, portCallGuid) => {
        const cargoMap = new Map<string, RotationCargoOperation>();
        for (const op of ops) {
            if (!cargoMap.has(op.cargoGuid)) {
                cargoMap.set(op.cargoGuid, op);
            }
        }
        transitOpsByPortAndCargo.set(portCallGuid, cargoMap);
    });

    stowagePlan.manifest.tankContentsByTankPerBerthCall.forEach((tankContent) => {
        const portCallGuid = tankContent.portCall.portCallGuid;
        const transitOpsForPort = transitOpsByPortAndCargo.get(portCallGuid);
        if (!transitOpsForPort) return;

        // Update cargo contents with transit operation references
        for (const cargoContent of tankContent.cargoContents) {
            if (!cargoContent.cargoOperation) {
                cargoContent.cargoOperation = transitOpsForPort.get(cargoContent.cargo.cargoGuid);
            }
        }
    });
};

/**
 * Filters out empty tank contents and zero weight entries
 */
const filterEmptyTankContents = (stowagePlan: StowagePlan): void => {
    const filteredEntries = new Map<string, TankContent>();

    stowagePlan.manifest.tankContentsByTankPerBerthCall.forEach((tankContent, key) => {
        // Keep only contents with weight > 0 or special operations
        // Using Decimal comparison instead of isPositive to avoid rounding issues
        const filteredContents = tankContent.cargoContents.filter(
            (c) => c.weight.gt(new Decimal(0)) || c.cargoOperation?.operationType === 'Unloading' || c.cargoOperation?.operationType === 'Transfer'
        );

        // Only keep tank content if it has any contents
        if (filteredContents.length > 0) {
            tankContent.cargoContents = filteredContents;
            filteredEntries.set(key, tankContent);
        }
    });

    stowagePlan.manifest.tankContentsByTankPerBerthCall = filteredEntries;
};

/**
 * Adds transit operations stowage items
 */
const addTransitOperationsStowageItems = (stowagePlan: StowagePlan): void => {
    const transitOperationsStowageItems: Stowage[] = [];
    const transitOpsMap = createTransitOpsMap(stowagePlan);

    // Process tank contents to create transit stowage items
    stowagePlan.manifest.tankContentsByTankPerBerthCall.forEach((tankContent) => {
        for (const cargoContent of tankContent.cargoContents) {
            // Skip if no transit weight or not a transit berth
            if (!isPositive(cargoContent.weightInTransit) || cargoContent.cargoOperation?.berthCall?.berthId !== -1) continue;

            const transitKey = `${tankContent.portCall.portCallGuid}-${cargoContent.cargo.cargoGuid}`;
            const transitCargoOperation = transitOpsMap.get(transitKey);

            if (transitCargoOperation && transitCargoOperation.berthCallGuid === tankContent.berthCall.berthCallGuid) {
                transitOperationsStowageItems.push(createTransitStowageItem(tankContent.tank, cargoContent, transitCargoOperation));
            }
        }
    });

    stowagePlan.transitStowageItems = transitOperationsStowageItems;
};

/**
 * Creates a map of transit operations by port call and cargo
 */
const createTransitOpsMap = (stowagePlan: StowagePlan): Map<string, RotationCargoOperation> => {
    const transitOpsMap = new Map<string, RotationCargoOperation>();

    stowagePlan.transitOperations.forEach((portCall) => {
        portCall.berthCalls.forEach((berthCall) => {
            berthCall.cargoOperations.forEach((op) => {
                const key = `${portCall.portCallGuid}-${op.cargoGuid}`;
                transitOpsMap.set(key, op);
            });
        });
    });

    return transitOpsMap;
};

/**
 * Creates a transit stowage item from cargo content
 */
const createTransitStowageItem = (tank: Tank, cargoContent: CargoContent, transitCargoOperation: RotationCargoOperation): Stowage => ({
    stowageGuid: crypto.randomUUID(),
    cargoOperation: transitCargoOperation,
    cargoOperationGuid: transitCargoOperation.cargoOperationGuid,
    tankId: tank.tankId,
    tank: tank,
    weight: cargoContent.weightInTransit,
    shipFigure: cargoContent.shipFigure,
    isShipFigureAdjusted: cargoContent.isShipFigureAdjusted,
});

// ============================================================
// Private Helper Functions
// ============================================================

/**
 * Gets previous tank contents in a tank
 */
const getPreviousTankContentsInTank = (stowagePlan: StowagePlan, tank: Tank, berthCall: RotationBerthCall): TankContent[] => {
    const currentTankContent = getTankContentInBerthCall(stowagePlan, berthCall, tank);
    const result: TankContent[] = [];

    if (!stowagePlan?.manifest?.tankContentsByTankPerBerthCall || !tank || !berthCall) return result;

    stowagePlan.manifest.tankContentsByTankPerBerthCall.forEach((tc) => {
        const beforeOrEqual = isBeforeOrEqual(stowagePlan.berthCalls, tc.berthCall, berthCall);

        if (tc.tank.tankId === tank.tankId && beforeOrEqual) {
            // Check if any cargo in current tank content matches cargo in this tank content
            let shouldInclude = true;
            if (currentTankContent?.cargoContents?.length) {
                const currentPositiveWeightCargoes = currentTankContent.cargoContents
                    .filter((c) => isPositive(c.weight))
                    .map((c) => c.cargo.cargoGuid);

                // Check if this tank content has any cargo that's also in current tank content
                const hasOverlap = tc.cargoContents.some((c) => currentPositiveWeightCargoes.includes(c.cargo.cargoGuid));
                shouldInclude = !hasOverlap;
            }

            if (shouldInclude) {
                result.push(tc);
            }
        }
    });

    return result;
};
